import type { GateResult } from "@iamu/shared";
import { ArrowRight, Check, Minus } from "lucide-react";
import { Link } from "react-router-dom";
import { VerdictPill } from "@/components/ui/verdict-indicator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScenarioRow } from "@/components/experience/scenario-row";

export interface ScenarioLabel {
  id: string;
  situation: string;
  persona: string;
  /** The situation's human tests; null/absent when unknown. */
  humanTests?: ReadonlyArray<{ id: string; question: string }> | null;
}

const DELTA_LABEL: Record<string, string> = {
  improved: "Improved",
  unchanged: "Unchanged",
  declined: "Declined",
  declined_to_no: "Fell to No",
  no_baseline: "First judged",
};

function Ratio({
  label,
  value,
  of,
}: {
  label: string;
  value: number;
  of: number;
}) {
  if (of === 0) {
    return (
      <div className="flex flex-col-reverse">
        <dt className="text-sm text-muted-foreground">{label}</dt>
        <dd className="text-lg font-semibold text-muted-foreground">None</dd>
      </div>
    );
  }
  const pct = Math.round((value / of) * 100);
  return (
    <div className="flex flex-col-reverse">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-lg font-semibold tabular-nums text-foreground">
        {value}/{of}
        <span className="ml-2 text-sm font-normal text-muted-foreground">
          {pct}%
        </span>
      </dd>
    </div>
  );
}

function questionOf(
  label: ScenarioLabel | undefined,
  humanTestId: string,
): string | null {
  const question = label?.humanTests
    ?.find((h) => h.id === humanTestId)
    ?.question?.trim();
  return question || null;
}

/**
 * A human test by its question, clamped to two lines with the full text on
 * hover. The id is only a fallback for tests the situation no longer has.
 */
function HumanTestLabel({
  id,
  question,
}: {
  id: string;
  question: string | null;
}) {
  if (!question) {
    return (
      <span className="font-mono text-xs text-muted-foreground">{id}</span>
    );
  }
  return (
    <span
      className="line-clamp-2 basis-full text-sm text-foreground md:max-w-[48ch]"
      title={question}
    >
      {question}
    </span>
  );
}

/**
 * Coverage: the three ratios, then one row per in-scope scenario — was it
 * re-run in this build, which run is its baseline (carried forward when
 * older), and how its human tests moved.
 */
export function GateCoverage({
  result,
  scenarios,
  runsByScenario,
  projectId,
}: {
  result: GateResult;
  scenarios: ScenarioLabel[];
  runsByScenario: Map<string, string[]>;
  projectId: string;
}) {
  const labelOf = new Map(scenarios.map((s) => [s.id, s]));
  const { coverage } = result;
  return (
    <div className="space-y-6">
      <dl className="flex flex-wrap gap-x-10 gap-y-4">
        <Ratio
          label="Scenarios re-run"
          value={coverage.scenarios.rerun}
          of={coverage.scenarios.in_scope}
        />
        <Ratio
          label="Human tests judged"
          value={coverage.human_tests.judged}
          of={coverage.human_tests.in_scope}
        />
        <Ratio
          label="Baseline problems re-checked"
          value={coverage.problems_rechecked.rechecked}
          of={coverage.problems_rechecked.baseline_open}
        />
      </dl>
      <Table aria-label="Coverage by scenario">
        <TableHeader>
          <TableRow>
            <TableHead>Scenario</TableHead>
            <TableHead>This build</TableHead>
            <TableHead className="hidden md:table-cell">Baseline run</TableHead>
            <TableHead className="hidden md:table-cell">Human tests</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {result.baseline.per_scenario.map((p) => {
            const label = labelOf.get(p.scenario_id);
            const runs = runsByScenario.get(p.scenario_id) ?? [];
            const deltas = result.verdict_deltas.filter(
              (d) => d.scenario_id === p.scenario_id,
            );
            const baselineCell = p.baseline_run_id ? (
              <>
                <Link
                  to={`/projects/${projectId}/walkthroughs/${p.baseline_run_id}`}
                  aria-label={`Baseline run: ${label?.situation ?? "scenario"}`}
                  className="text-foreground underline underline-offset-4"
                >
                  Baseline run
                </Link>
                {p.carried_forward && (
                  <div className="text-sm text-muted-foreground">
                    Carried forward
                  </div>
                )}
              </>
            ) : (
              <span className="text-muted-foreground">No baseline</span>
            );
            const deltaList =
              deltas.length === 0 ? null : (
                <ul className="space-y-1.5">
                  {deltas.map((d) => (
                    <li
                      key={d.human_test_id}
                      className="flex flex-wrap items-center gap-1.5 text-sm"
                    >
                      <HumanTestLabel
                        id={d.human_test_id}
                        question={questionOf(label, d.human_test_id)}
                      />
                      {d.baseline && <VerdictPill rating={d.baseline} />}
                      {d.baseline && (
                        <ArrowRight
                          className="size-3.5 text-muted-foreground"
                          aria-label="to"
                        />
                      )}
                      <VerdictPill rating={d.candidate} />
                      <span className="text-muted-foreground">
                        {DELTA_LABEL[d.delta]}
                      </span>
                    </li>
                  ))}
                </ul>
              );
            return (
              <TableRow key={p.scenario_id}>
                <TableCell className="align-top whitespace-normal">
                  <ScenarioRow
                    variant="compact"
                    scenario={{
                      id: p.scenario_id,
                      name: label?.situation ?? "Scenario",
                      description: label?.persona ?? null,
                    }}
                    href={`/projects/${projectId}/scenarios/${p.scenario_id}`}
                    className="p-0 hover:bg-transparent"
                  />
                  <div className="mt-2 space-y-2 md:hidden">
                    {baselineCell}
                    {deltaList}
                  </div>
                </TableCell>
                <TableCell className="align-top whitespace-nowrap">
                  {runs.length > 0 ? (
                    <Link
                      to={`/projects/${projectId}/walkthroughs/${runs[runs.length - 1]}`}
                      aria-label={`Re-run in this build: ${label?.situation ?? "scenario"}`}
                      className="inline-flex items-center gap-1.5 text-foreground underline underline-offset-4"
                    >
                      <Check className="size-4" aria-hidden="true" />
                      Re-run{runs.length > 1 ? ` ${runs.length}×` : ""}
                    </Link>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                      <Minus className="size-4" aria-hidden="true" />
                      Not re-run
                    </span>
                  )}
                </TableCell>
                <TableCell className="hidden align-top md:table-cell">
                  {baselineCell}
                </TableCell>
                <TableCell className="hidden align-top md:table-cell">
                  {deltaList ?? (
                    <span className="text-muted-foreground">–</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
